import React from 'react';
import { Link } from 'react-router-dom';
import { GoToLink } from '@shared/ui/go-to-link';
import { ImageWithFallback } from '@shared/ui/image-with-fallback';
import noAvatar from '@shared/assets/images/no_avatar.svg';
import moment from 'moment';
import { ContactInfo } from '@shared/ui/contact-info';
import { EmptyState } from '@shared/ui/empty-state';
import { getIcon } from '../assets/getIcon';

// ─── DashboardItem ─────────────────────────────────────────────────────────────

interface DashboardItemProps {
  status: string;
  children: React.ReactNode;
  title: string;
  link: string;
  className?: string;
  flex?: number | string;
  type: string;
}

function DashboardItem({ status, children, title, link, className = '', flex, type }: DashboardItemProps) {
  const flexStyle = flex ? { flex } : {};

  if (status === 'loading') {
    return (
      <div className={`min-h-[380px] h-full ${className}`}>
        <div className="animate-pulse bg-gray-200 rounded-2xl h-full min-h-[380px]" />
      </div>
    );
  }

  return (
    <div className={`min-h-[380px] h-full ${className}`}>
      <div
        className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)] border border-gray-100 hover:shadow-[0_4px_20px_rgba(0,0,0,0.12)]
        transition-all duration-300 hover:-translate-y-1 p-6 flex flex-col h-full"
        style={flexStyle}
      >
        <div className="flex items-center gap-3 mb-6 flex-shrink-0">
          <div className="w-7 h-7 bg-gradient-to-br from-red-500 to-red-600 rounded-[10px] shadow-md flex justify-center items-center">
            <img className="w-4 h-4 brightness-0 invert" src={getIcon(type)} alt="" />
          </div>
          <h3 className="text-xl font-bold text-gray-800">{title}</h3>
        </div>
        <div className="flex-1 overflow-hidden min-h-0">{children}</div>
        <div className="mt-6 pt-4 border-t border-gray-100 flex-shrink-0">
          <GoToLink to={link} text="Показать все" />
        </div>
      </div>
    </div>
  );
}

// ─── DashboardUserItem ──────────────────────────────────────────────────────────

export function DashboardUserItem({ item, link, state }: { item: any; link: string; state: any }) {
  return (
    <Link
      to={`${link}/${item.id}`}
      state={state}
      key={item.id}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
    >
      <ImageWithFallback
        className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-200 group-hover:ring-red-300 transition-all duration-200"
        fallbackSrc={noAvatar}
        defaultSrc={noAvatar}
        src={item.pho}
      />
      <p className="font-medium text-gray-800 group-hover:text-red-600 transition-colors duration-200">
        {item.first_name + ' ' + item.last_name || item.name}
      </p>
    </Link>
  );
}

// ─── DashboardCompanyItem ───────────────────────────────────────────────────────

// На дашборде этот компонент рендерит юр. лица (см. Dashboard.tsx,
// данные из selectLegalEntities). Имя «Company» — историческое, его не
// переименовываем, чтобы не трогать импорты, но сделали `to`
// настраиваемым: для FMeter-staff клик уводит на /companies (как
// раньше), для клиентов-OWNER/USER — на карточку их собственной
// компании, а не на пустую/запрещённую страницу пула клиентов FMeter.
export function DashboardCompanyItem({
  item,
  to = 'companies/',
}: {
  item: any;
  to?: string;
}) {
  return (
    <Link
      to={to}
      key={item.id}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
    >
      <ImageWithFallback
        className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-200 group-hover:ring-red-300 transition-all duration-200"
        fallbackSrc={noAvatar}
        defaultSrc={noAvatar}
        src={item.photo}
      />
      <p className="font-medium text-gray-800 group-hover:text-red-600 transition-colors duration-200">
        {item.name}
      </p>
    </Link>
  );
}

// ─── DashboardOrderItem ─────────────────────────────────────────────────────────

export function DashboardOrderItem({ order }: { order: any }) {
  return (
    <div key={order.id} className="p-4">
      <div className="text-sm font-semibold text-gray-600 mb-2">
        Заказ от {moment(order.createdAt).format('D MMMM')}
      </div>
      <div className="flex justify-between items-center gap-2 text-blue-600 text-sm font-medium">
        <span className="truncate">{order.name}</span>
        <span className="text-xs px-2 py-1 rounded-full">{order?.legalEntity?.name}</span>
      </div>
    </div>
  );
}

// ─── DashboardCatalogCard ───────────────────────────────────────────────────────

export function DashboardCatalogCard({ item }: { item: any }) {
  return (
    <Link
      to={`catalog/${item.id}`}
      key={item.id}
      className="group bg-white hover:shadow-lg transition-all duration-300 overflow-hidden w-[209px] h-[246px]"
    >
      <div className="h-full bg-[#F6F7FA] rounded-2xl flex flex-col">
        <div className="flex justify-center items-center pt-4">
          <ImageWithFallback
            src={item.photo}
            className="w-[132px] h-[132px] object-cover rounded-lg group-hover:scale-105 transition-transform duration-300"
            alt="dashboard-catalog card"
          />
        </div>
        <div className="p-3 flex-1 flex items-center">
          <p className="font-medium text-center text-sm text-gray-800 group-hover:text-blue-600 transition-colors duration-200 line-clamp-2 w-full">
            {item.name}
          </p>
        </div>
      </div>
    </Link>
  );
}

// ─── DashboardManagerCard ───────────────────────────────────────────────────────

export function DashboardManagerCard({ manager }: { manager: any }) {
  return (
    <Link
      to={`catalog/${manager.id}`}
      className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.12)] transition-all duration-300 p-4 border border-gray-100 hover:border-gray-200 group"
    >
      <div className="flex items-center gap-3 mb-3">
        <img
          className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-200 group-hover:ring-blue-300 transition-all duration-200"
          src={manager?.photo ? manager?.photo : noAvatar}
          alt="manager"
        />
        <p className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors duration-200">
          {manager?.name} {manager?.surname}
        </p>
      </div>
      {manager && <ContactInfo contact={manager} />}
    </Link>
  );
}

// ─── DashboardPriceItem ─────────────────────────────────────────────────────────

export function DashboardPriceItem({ price }: { price: any }) {
  return (
    <Link
      to={`prices/${price.id}`}
      state={{ price }}
      key={price.id}
      className="block p-3 border-l-[10px] border-red-500"
    >
      <span className="font-medium text-gray-800 transition-colors duration-200">{price.name}</span>
    </Link>
  );
}

// ─── DashboardCartTable ─────────────────────────────────────────────────────────

export function DashboardCartTable({ cart }: { cart: any }) {
  return cart.length ? (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Наименование товара
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Количество
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {cart.slice(0, 5).map((item: any) => (
            <tr key={item.id} className="hover:bg-gray-50 transition-colors duration-200">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <ImageWithFallback
                    src={item.photo}
                    className="w-10 h-10 rounded-lg object-cover"
                    alt=""
                  />
                  <span className="font-medium text-gray-800 truncate">{item.name}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {item.quantity}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <EmptyState message="Корзина пуста" />
  );
}

// ─── DashboardCatalogCarousel ───────────────────────────────────────────────────

export function DashboardCatalogCarousel({ items }: { items: any[] }) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [itemsPerView, setItemsPerView] = React.useState(5);

  React.useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth < 640) setItemsPerView(1);
      else if (window.innerWidth < 1024) setItemsPerView(2);
      else if (window.innerWidth < 1280) setItemsPerView(3);
      else if (window.innerWidth < 1536) setItemsPerView(4);
      else setItemsPerView(5);
    };
    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  if (!items || items.length === 0) {
    return <EmptyState message="Товаров в каталоге нет" />;
  }

  if (items.length <= itemsPerView) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((item) => (
          <DashboardCatalogCard key={item.id} item={item} />
        ))}
      </div>
    );
  }

  const maxIndex = items.length - itemsPerView;
  const totalSlides = maxIndex + 1;
  const nextSlide = () => setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  const prevSlide = () => setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));

  return (
    <div className="relative">
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${(currentIndex * 100) / itemsPerView}%)` }}
        >
          {items.map((item) => (
            <div key={item.id} className="flex-shrink-0 p-2" style={{ width: `${100 / itemsPerView}%` }}>
              <DashboardCatalogCard item={item} />
            </div>
          ))}
        </div>
      </div>
      <button
        onClick={prevSlide}
        className="absolute left-1 top-1/2 -translate-y-1/2 -translate-x-1 bg-white rounded-full p-1.5 group border border-gray-200 hover:border-gray-300 shadow-md"
        aria-label="Previous slide"
      >
        <svg className="w-4 h-4 text-gray-600 group-hover:text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-1 top-1/2 -translate-y-1/2 translate-x-1 bg-white rounded-full p-1.5 group border border-gray-200 hover:border-gray-300 shadow-md"
        aria-label="Next slide"
      >
        <svg className="w-4 h-4 text-gray-600 group-hover:text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      <div className="flex justify-center mt-3 space-x-1.5">
        {Array.from({ length: totalSlides }).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
              index === currentIndex ? 'bg-red-500 w-4' : 'bg-gray-300 hover:bg-gray-400'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default React.memo(DashboardItem);
